/**
 * Flatten Django / DRF validation errors into displayable strings.
 * Supports shapes like:
 * { classification: ["\"value\" is not a valid choice."] }
 * { customer: { customer_number: ["already exists"] } }
 * { error: "..." } | { detail: "..." } | { messages: [{ message: "..." }] }
 */
export function extractApiErrorMessages(data) {
  if (data == null) return [];

  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(data)) {
    return data.flatMap((item) => {
      if (typeof item === "string") {
        return item.trim() ? [item] : [];
      }
      if (item && typeof item === "object" && item.message) {
        return extractApiErrorMessages(item.message);
      }
      return extractApiErrorMessages(item);
    });
  }

  if (typeof data === "object") {
    if (typeof data.error === "string" && data.error.trim()) {
      return [data.error];
    }

    if (data.detail != null) {
      const detailMessages = extractApiErrorMessages(data.detail);
      if (detailMessages.length) return detailMessages;
    }

    if (Array.isArray(data.messages)) {
      const legacyMessages = data.messages
        .map((entry) => (typeof entry === "string" ? entry : entry?.message))
        .filter((msg) => typeof msg === "string" && msg.trim());
      if (legacyMessages.length) return legacyMessages;
    }

    if (Array.isArray(data.non_field_errors)) {
      const nonFieldMessages = extractApiErrorMessages(data.non_field_errors);
      if (nonFieldMessages.length) return nonFieldMessages;
    }

    const fieldMessages = [];
    for (const [key, value] of Object.entries(data)) {
      if (["error", "detail", "messages"].includes(key)) continue;
      fieldMessages.push(...extractApiErrorMessages(value));
    }
    return fieldMessages;
  }

  return [];
}

export function getApiErrorMessage(data, fallback = "") {
  const messages = extractApiErrorMessages(data);
  return messages[0] || fallback;
}

export function showApiErrorToasts(toast, data, fallback) {
  const messages = [...new Set(extractApiErrorMessages(data).filter(Boolean))];

  if (!messages.length) {
    if (fallback) toast.error(fallback);
    return;
  }

  messages.forEach((message) => toast.error(message));
}
